/**
 * The service and test catalogue — what a laboratory sells.
 *
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANYTHING HERE.
 *
 * This file describes a catalogue entry COMMERCIALLY: what it is called, which
 * published standard it is performed to, what kind of material it is performed
 * on, how long it takes, what it costs and how it is charged. Every one of
 * those is a fact a laboratory's price list already states.
 *
 * It describes NOTHING SCIENTIFIC. There is no input field, no reading, no
 * unit of measurement, no formula, no calculation order, no rounding rule, no
 * acceptance limit, no chart, no axis, no Excel cell mapping and no
 * specialised result or report layout — not for the five initial tests and not
 * for any test. Those arrive one test at a time, in Phase 13, each behind a
 * laboratory-validated worked example. A type added here that implied any of
 * them would be an invented scientific claim wearing a TypeScript interface.
 *
 * `template_readiness` is the honest name for the gap. A test that is
 * `not_ready` says so, on screen, and uses the general workflow only.
 */
// ---------------------------------------------------------------------------
// Sample type
// ---------------------------------------------------------------------------
/**
 * The codes of the seven BASELINE sample types every laboratory is given
 * (0024 seeds them, 0025 marks them `is_system`).
 *
 * NOT the list of sample types. A laboratory's types are its own
 * configuration (Settings -> Sample types, D296-D305), and since 0025 the
 * catalogue classifies by them too (D306): a catalogue entry's `sampleType`
 * is any code of the laboratory's own types, checked by the API and by a
 * tenant-scoped foreign key. These constants exist only so code that seeds a
 * development catalogue can name a baseline type without a magic string.
 *
 * A classification, not a specification: it says a test is performed on
 * soil, never how, with what, or to what limit.
 */
export const SAMPLE_TYPE = {
    SOIL: 'soil',
    CONCRETE: 'concrete',
    ASPHALT: 'asphalt',
    AGGREGATE: 'aggregate',
    STEEL: 'steel',
    WATER: 'water',
    OTHER: 'other',
};
// ---------------------------------------------------------------------------
// Pricing unit
// ---------------------------------------------------------------------------
/**
 * What one unit of this service IS, for the purpose of charging.
 *
 * COMMERCIAL, not scientific. `PER_POINT` means the laboratory bills per
 * agreed point of work, and this product deliberately does not know what a
 * point consists of for any given test — that is exactly the kind of detail
 * section 4 refuses to invent. The laboratory writes it in the entry's own
 * name and notes, in its own words.
 */
export const PRICING_UNIT = {
    PER_TEST: 'per_test',
    PER_SAMPLE: 'per_sample',
    PER_POINT: 'per_point',
    PER_HOUR: 'per_hour',
    PER_DAY: 'per_day',
    /** A single agreed price for the whole item, regardless of quantity. */
    LUMP_SUM: 'lump_sum',
};
export const ALL_PRICING_UNITS = Object.freeze(Object.values(PRICING_UNIT));
// ---------------------------------------------------------------------------
// Expected duration
// ---------------------------------------------------------------------------
/**
 * The unit an expected turnaround is quoted in.
 *
 * A duration is a VALUE AND A UNIT, stored together, because a laboratory
 * quotes "three working days" and storing 72 hours would invent a precision —
 * and a definition of a day — that nobody stated.
 */
export const DURATION_UNIT = {
    HOUR: 'hour',
    DAY: 'day',
    WEEK: 'week',
};
export const ALL_DURATION_UNITS = Object.freeze(Object.values(DURATION_UNIT));
// ---------------------------------------------------------------------------
// The scientific gate, expressed as a type-level fact
// ---------------------------------------------------------------------------
/**
 * Every field a catalogue entry is permitted to carry.
 *
 * This list exists so that a test can assert the API returns NOTHING ELSE. It
 * is the machine-readable form of CLAUDE.md section 4: if a future change adds
 * `inputFields`, `formula`, `rounding`, `acceptanceLimits`, `chart` or an
 * Excel mapping to a catalogue response, the assertion that the response keys
 * are a subset of this list fails, and the merge gate the plan names holds.
 *
 * Adding a name here is therefore a deliberate act, not a formality.
 */
export const CATALOGUE_ITEM_FIELDS = Object.freeze([
    'id',
    'code',
    'name',
    'category',
    'department',
    'referenceStandard',
    'standardVersion',
    'sampleType',
    'expectedDuration',
    'basePriceMinor',
    'currency',
    'pricingUnit',
    'requiredEquipment',
    'reportLanguages',
    'templateReadiness',
    'resultTemplateVersion',
    'reportTemplateReference',
    'reportTemplateId',
    'notes',
    'isActive',
    'deactivatedAt',
    'createdAt',
    'updatedAt',
    /**
     * Told plainly rather than left for the interface to infer from
     * `templateReadiness`: this entry has no specialised template, so it uses
     * the general workflow and shows no scientific fields.
     */
    'usesGeneralWorkflowOnly',
]);
/**
 * Words that must never appear as a field name on a catalogue entry until a
 * validated worked example exists for the test in question.
 *
 * Used by the merge-gate test. It is a blunt instrument on purpose: the point
 * is that somebody adding `sieveSizes` or `roundingRule` in a hurry trips over
 * a failing test rather than over a code review that may not happen.
 */
export const FORBIDDEN_SCIENTIFIC_FIELD_MARKERS = Object.freeze([
    'formula',
    'calculation',
    'rounding',
    'acceptancelimit',
    'acceptancecriteria',
    'passfail',
    'chart',
    'axis',
    'inputfield',
    'reading',
    'sieve',
    'moisture',
    'atterberg',
    'proctor',
    'cbr',
    'penetration',
    'excelmapping',
    'cellmapping',
    'specimen',
    'unitofmeasure',
    'tolerance',
]);
//# sourceMappingURL=catalogue.js.map